import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";

import { Badge, Button, Column, Small, Table } from "@fronterahealth/frontera-ui-components";

import {
  CaregiverType,
  GetCaregiversDataDocument,
  ProviderType,
  useDisconnectCaregiverFromOrganizationMutation,
  useSendCaregiverActivationCodeNotificationMutation,
} from "@api/graphql/types-and-hooks";
import { CopyToClipboardButton } from "@components/CopyToClipboardButton/CopyToClipboardButton";
import { TableFilterDropdown } from "@components/TableFilterDropdown/TableFilterDropdown";
import { UserInitialsView } from "@components/UserInitial/UserInitialsView";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useCursorPagination } from "@hooks/useCursorPagination";
import { BulkInviteCaregiversModal } from "@pages/Members/components/BulkInviteCaregiversModal";
import { InviteCaregiverModal } from "@pages/Members/components/InviteCaregiverModal";
import { CaregiverAppStatus, MemberCaregiver } from "@pages/Members/types";
import { buildBcbaNameByProviderId, mapCaregiverToMemberCaregiver } from "@pages/Members/utils/caregiverMapping";
import { useAdminData } from "@providers/AdminDataProvider";
import { useProvidersData } from "@providers/ProvidersDataProvider";

const PAGE_SIZE = 10;

const appStatusBadge = (status: CaregiverAppStatus) => {
  switch (status) {
    case CaregiverAppStatus.NotInvited:
      return <Badge appearance="neutral" text="Not invited" />;
    case CaregiverAppStatus.CodeSent:
      return <Badge appearance="warn" text="Code sent" />;
    case CaregiverAppStatus.Connected:
      return <Badge appearance="success" text="Connected" />;
  }
};

const copyActivationCode = (code: string) => {
  navigator.clipboard.writeText(code);
  notifySuccess("Activation code copied!");
};

const STATUS_FILTER_ITEMS = [
  { id: CaregiverAppStatus.NotInvited, label: "Not invited" },
  { id: CaregiverAppStatus.CodeSent, label: "Code sent" },
  { id: CaregiverAppStatus.Connected, label: "Connected" },
];

const STATUS_TRUTH: Record<CaregiverAppStatus, { invited?: boolean; connected: boolean }> = {
  [CaregiverAppStatus.NotInvited]: { invited: false, connected: false },
  [CaregiverAppStatus.CodeSent]: { invited: true, connected: false },
  [CaregiverAppStatus.Connected]: { connected: true },
};

interface CaregiversTabProps {
  buttonContainer: HTMLDivElement | null;
}

export const CaregiversTab: React.FC<CaregiversTabProps> = ({ buttonContainer }) => {
  const navigate = useNavigate();
  const { orgId } = useAdminData();
  const [statusFilter, setStatusFilter] = useState<CaregiverAppStatus[]>([]);
  const [bcbaFilter, setBcbaFilter] = useState<string[]>([]);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [bulkInviteModalOpen, setBulkInviteModalOpen] = useState(false);
  const [preselectedCaregiverId, setPreselectedCaregiverId] = useState<string | undefined>();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 200);

  const { providersQuery } = useProvidersData();

  const invitedValues = useMemo(
    () => new Set(statusFilter.map((s) => STATUS_TRUTH[s].invited).filter((v): v is boolean => v !== undefined)),
    [statusFilter],
  );
  const connectedValues = useMemo(() => new Set(statusFilter.map((s) => STATUS_TRUTH[s].connected)), [statusFilter]);

  const {
    data: caregiverNodes,
    isFetching,
    getCurrentPageData,
    totalItemCount,
    refetch: refetchCaregivers,
  } = useCursorPagination({
    queryKey: "getCaregiversDataQuery",
    queryVariables: {
      filter: {
        searchTerm: debouncedSearchTerm || undefined,
        linkedProviderIds: bcbaFilter.length ? bcbaFilter : undefined,
        isInvited: invitedValues.size === 1 ? [...invitedValues][0] : undefined,
        isConnected: connectedValues.size === 1 ? [...connectedValues][0] : undefined,
      },
    },
    watchedVariables: [debouncedSearchTerm, bcbaFilter, statusFilter],
    document: GetCaregiversDataDocument,
    responseKey: "caregivers",
    pageSize: PAGE_SIZE,
  });

  const bcbaNameByProviderId = useMemo(
    () => buildBcbaNameByProviderId((providersQuery.data?.provider || []) as ProviderType[]),
    [providersQuery.data],
  );

  const caregivers = useMemo(
    () =>
      ((caregiverNodes || []) as CaregiverType[])
        .map((node) => mapCaregiverToMemberCaregiver(node, bcbaNameByProviderId))
        .filter((c): c is MemberCaregiver => c !== null),
    [caregiverNodes, bcbaNameByProviderId],
  );

  const sendActivationCode = useSendCaregiverActivationCodeNotificationMutation({
    onSuccess: (res) => {
      if (!res.sendCaregiverActivationCodeNotification?.status) {
        notifyError(res.sendCaregiverActivationCodeNotification?.message || "Something went wrong sending the code.");
        return;
      }
      notifySuccess(res.sendCaregiverActivationCodeNotification?.message || "Activation code sent.");
      refetchCaregivers();
    },
    onError: () => {
      notifyError("Something went wrong sending the activation code.");
    },
  });

  const disconnectCaregiver = useDisconnectCaregiverFromOrganizationMutation({
    onSuccess: (res) => {
      if (!res.disconnectCaregiverFromOrganization?.success) {
        notifyError(res.disconnectCaregiverFromOrganization?.message || "Something went wrong revoking access.");
        return;
      }
      notifySuccess(res.disconnectCaregiverFromOrganization?.message || "Access revoked.");
      refetchCaregivers();
    },
    onError: () => {
      notifyError("Something went wrong revoking access.");
    },
  });

  // Derived from the full provider list (not just the current page of caregivers) so the
  // filter always reflects every BCBA org-wide, regardless of which page is loaded.
  // Keyed by provider id (not name) since that's what the backend filter takes.
  const bcbaFilterItems = useMemo(
    () => Array.from(bcbaNameByProviderId.entries()).map(([id, name]) => ({ id, label: name })),
    [bcbaNameByProviderId],
  );

  const columns: Column<MemberCaregiver>[] = [
    {
      dataIndex: "name",
      key: "name",
      title: "Name",
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <UserInitialsView
            firstName={record.firstName}
            lastName={record.lastName}
            className="!h-8 !w-8 !max-h-8 !max-w-8 text-xs"
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Small displayType="loud" className="text-text-primary">
                {record.name}
              </Small>
              {record.isPrimary && <Badge appearance="success" text="Primary" />}
            </div>
            <Small colorType="secondary" className="truncate text-xs">
              {record.linkedChildren.map((c) => c.name).join(", ") || "No linked child"}
            </Small>
          </div>
        </div>
      ),
    },
    {
      dataIndex: "email",
      key: "email",
      title: "Contact",
      render: (_, record) => (
        <div className="flex flex-col">
          <Small>{record.email || "—"}</Small>
          {record.phone && <Small colorType="secondary">{record.phone}</Small>}
        </div>
      ),
    },
    {
      dataIndex: "activationCode",
      key: "activationCode",
      title: "Activation Code",
      render: (_, record) => {
        if (!record.activationCode) return <Small>{"—"}</Small>;
        return (
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-sm text-text-primary">{record.activationCode}</span>
            <CopyToClipboardButton text={record.activationCode} />
          </div>
        );
      },
    },
    {
      dataIndex: "appStatus",
      key: "appStatus",
      title: "App Status",
      className: "whitespace-nowrap",
      render: (_, record) => appStatusBadge(record.appStatus),
    },
  ];

  const getActions = (item: MemberCaregiver) => [
    item.appStatus === CaregiverAppStatus.NotInvited && {
      label: "Invite",
      callback: () => {
        setPreselectedCaregiverId(item.id);
        setInviteModalOpen(true);
      },
    },
    item.appStatus === CaregiverAppStatus.CodeSent && {
      label: "Resend code",
      callback: () => {
        if (!orgId) return;
        sendActivationCode.mutate({ caregiverId: item.id, organizationId: orgId });
      },
    },
    item.appStatus === CaregiverAppStatus.CodeSent && {
      label: "Copy code",
      callback: () => {
        if (item.activationCode) {
          copyActivationCode(item.activationCode);
        }
      },
    },
    {
      label: "Edit",
      callback: () => {
        navigate(`/members/caregiver/${item.id}`);
      },
    },
    item.linkedChildren.length > 0 && {
      label: "View client",
      callback: () => {
        navigate(`/clients/${item.linkedChildren[0].id}`);
      },
    },
    item.appStatus === CaregiverAppStatus.Connected &&
      !!orgId && {
        label: "Revoke access",
        callback: () => {
          disconnectCaregiver.mutate({ organization: orgId, caregiver: item.id });
        },
      },
  ];

  const handleRowClick = (caregiver: MemberCaregiver) => {
    navigate(`/members/caregiver/${caregiver.id}`);
  };

  const actionButtons = (
    <>
      <Button
        text="Bulk Invite"
        variant="secondary"
        buttonStyle="border"
        onClick={() => setBulkInviteModalOpen(true)}
      />
      <Button
        text="Invite Caregiver"
        variant="primary"
        onClick={() => {
          setPreselectedCaregiverId(undefined);
          setInviteModalOpen(true);
        }}
      />
    </>
  );

  return (
    <div className="w-full">
      {buttonContainer && createPortal(actionButtons, buttonContainer)}

      {/* Table */}
      <div className="mt-4 min-h-[420px] w-full overflow-x-auto [&>div>div>div]:overflow-visible">
        <Table
          onRowClick={handleRowClick}
          defaultItemsPerPage={PAGE_SIZE}
          showPagination
          showSearch
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          // only show loading state on first page render
          loading={isFetching && caregivers.length === 0}
          itemsPerPageOptions={[5, 10, 25]}
          onPageChange={getCurrentPageData}
          totalItemCount={totalItemCount}
          actions={getActions}
          data={caregivers}
          columns={columns}
          headerContent={
            <div className="flex items-center gap-2">
              <TableFilterDropdown<string>
                label="BCBA"
                items={bcbaFilterItems}
                value={bcbaFilter}
                onChange={setBcbaFilter}
              />
              <TableFilterDropdown<CaregiverAppStatus>
                label="Status"
                items={STATUS_FILTER_ITEMS}
                value={statusFilter}
                onChange={setStatusFilter}
              />
            </div>
          }
        />
      </div>

      <InviteCaregiverModal
        isOpen={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        preselectedCaregiverId={preselectedCaregiverId}
      />
      <BulkInviteCaregiversModal isOpen={bulkInviteModalOpen} onClose={() => setBulkInviteModalOpen(false)} />
    </div>
  );
};
